import { FunctionComponent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ProcessRunState,
  ActivityRunState,
  ActivityRunResult,
} from '@robocorp/robocloud-orchestrator-interface';
import { Badge, BadgeProps, BadgeVariant, Tooltip } from '@robocorp/components';
import {
  IconExpand,
  IconStatusCompleted,
  IconStatusError,
  IconStatusNew,
  IconStatusPending,
  IconStatusTimeout,
  IconType,
} from '@robocorp/icons';
import { Color } from '@robocorp/theme';
import { IconLoading } from '@robocorp/icons/animated';

type OwnProps = {
  state?: `${ActivityRunState}`;
  result?: `${ActivityRunResult}`;
  errorCode?: string;
  label?: ReactNode;
  forwardedAs?: 'button';
} & Pick<BadgeProps, 'size' | 'iconAfter'>;

export const runVariants: Record<
  ActivityRunResult | ActivityRunState,
  {
    variant: BadgeVariant;
    icon: IconType;
    iconColor: Color;
  }
> = {
  [ActivityRunResult.Timeout]: {
    variant: 'primary',
    icon: IconStatusTimeout,
    iconColor: 'content.subtle',
  },
  [ActivityRunResult.StartFailed]: {
    variant: 'danger',
    icon: IconStatusError,
    iconColor: 'background.error',
  },
  [ActivityRunResult.ConnectionLoss]: {
    variant: 'danger',
    icon: IconStatusError,
    iconColor: 'background.error',
  },
  [ActivityRunResult.Error]: {
    variant: 'danger',
    icon: IconStatusError,
    iconColor: 'background.error',
  },
  [ActivityRunResult.Terminated]: {
    variant: 'danger',
    icon: IconStatusError,
    iconColor: 'background.error',
  },
  [ActivityRunResult.Ok]: {
    variant: 'success',
    icon: IconStatusCompleted,
    iconColor: 'background.success',
  },
  [ActivityRunState.New]: {
    variant: 'primary',
    icon: IconStatusNew,
    iconColor: 'content.subtle',
  },
  [ActivityRunState.InProgress]: {
    variant: 'primary',
    icon: IconLoading,
    iconColor: 'content.subtle',
  },
  [ProcessRunState.Complete]: {
    variant: 'success',
    icon: IconStatusCompleted,
    iconColor: 'background.success',
  },
  [ActivityRunState.AwaitingRuntime]: {
    variant: 'primary',
    icon: IconLoading,
    iconColor: 'content.subtle',
  },
};

export const StepRunAvatar: FunctionComponent<OwnProps> = ({
  errorCode,
  result,
  state,
  label: defaultLabel,
  ...rest
}) => {
  const { t } = useTranslation();

  const variantKey = result || state;

  const { icon, iconColor, variant } = variantKey
    ? runVariants[variantKey]
    : {
        variant: 'primary' as BadgeVariant,
        icon: IconStatusPending,
        iconColor: 'content.subtle' as Color,
      };

  const label = result ? t(`stepRunResult.${result}`) : t(`stepRunState.${state}`);

  return (
    <Tooltip text={errorCode}>
      <Badge
        icon={icon}
        iconColor={iconColor}
        variant={variant}
        label={defaultLabel || label}
        {...rest}
        iconAfter={IconExpand}
      />
    </Tooltip>
  );
};
